import React, { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { KButton, KDropdown, KInput, KSpan, KTooltip } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

import { SetURLSearchParams } from "react-router-dom"

import AdminTakedownLog from "../takedown-logs/AdminTakedownLog"
import Loading from "../../../../ui/Loading"
import Pagination from "../../../../ui/table/Pagination"
import { crawlers } from "../../../../../apis"
import { CLAIM_LOG_STATUS, errorFilledToastOptions } from "../../../../../constants"
import { ClaimLog } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"

const COLUMNS = ["ID", "DATE", "ACCOUNT", "APPLICANT", "RELEASE", "CODES", "DOCUMENTS", "USES", "STATUS"]

// One track list, shared by the header and every row so the two cannot drift apart.
//
// A column holding something of a fixed size is given that size in px - the id, the date, the codes,
// and above all the status pills, which need the width of three buttons. The columns holding text are
// fr, so they are what gives way as the window narrows. Sized in percentages instead, the pills had
// no way to fit in their share and pushed the whole table past its own container: Closed ended up off
// screen entirely and In Progress was clipped down the middle.
const COLUMN_TEMPLATE =
  "44px 88px minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1.3fr) 128px 92px 60px 270px"
// A grid track can be told to shrink, but the cell sitting in it still has to be told to let its own
// text be cut short rather than widen the track from the inside.
const cellStyle = { minWidth: 0 }

// The queue itself. NEW and IN_PROGRESS are what the warning strip on the User Asset page counts, so
// a claim somebody has picked up still shows as work outstanding until it is closed.
const statusOptions: KSelectOption[] = [
  { label: "All", value: 0, value2: "" },
  { label: "New", value: 1, value2: CLAIM_LOG_STATUS.NEW },
  { label: "In Progress", value: 2, value2: CLAIM_LOG_STATUS.IN_PROGRESS },
  { label: "Closed", value: 3, value2: CLAIM_LOG_STATUS.CLOSED }
]
// The pills on each row, in the order a claim moves through them.
const statusActions: KSelectOption[] = [
  { label: "New", value: 0, value2: CLAIM_LOG_STATUS.NEW },
  { label: "In Progress", value: 1, value2: CLAIM_LOG_STATUS.IN_PROGRESS },
  { label: "Closed", value: 2, value2: CLAIM_LOG_STATUS.CLOSED }
]

type AdminClaimLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
}

/**
 * What artists have claimed through the Co-Manager: somebody else's copy of their own record.
 *
 * Everything a takedown needs is on the row, because the point of the claim is that nobody has to
 * come back and ask: whose account it is and who it is filed under, the ISRC and the UPC every
 * platform's rights form asks for, the Drive folder holding the document that shows the rights, and
 * the links of the copies themselves.
 */
const AdminClaimLog: FC<AdminClaimLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [claims, setClaims] = useState<ClaimLog[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [limit] = useState(10)
  const [selectedStatus, setSelectedStatus] = useState<KSelectOption>(statusOptions[0])
  // Searched on the UPC, which is the code the rights team has in front of them when they come
  // looking for one claim: it is what the platform's form asked them for.
  const [search, setSearch] = useState("")
  // The claim being looked at. Opening one leaves the list for its own page, the way a DRM summary
  // opens its takedown logs: the links, the note and the check that was run are all too long for a
  // table cell, and the check is a table of its own.
  const [selectedClaim, setSelectedClaim] = useState<ClaimLog | null>(null)

  const fetchClaims = (nextOffset: number, status?: string, upc?: string) => {
    const statusParam = status ? `&status=${status}` : ""
    const searchParam = upc?.trim() ? `&search=${encodeURIComponent(upc.trim())}` : ""
    setLoading(true)
    crawlers(`/claim-logs/?offset=${nextOffset}&limit=${limit}${statusParam}${searchParam}`, "GET")
      .then((response) => {
        setClaims(mapListToCamelCase(response.data.results))
        setCount(response.data.count)
      })
      .catch(() => toast.error("The claim logs could not be loaded.", errorFilledToastOptions))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchClaims(0, selectedStatus.value2, search)
  }, [])

  const applyFilters = () => {
    setOffset(0)
    fetchClaims(0, selectedStatus.value2, search)
  }

  /**
   * Moves a claim along, with whatever the person working it wants written down.
   *
   * The note is asked for the way the takedown and exclusivity screens ask for theirs, and it is
   * optional: cancelling the prompt cancels the move, an empty note moves it with nothing written.
   */
  const moveTo = (claim: ClaimLog, status: string) => {
    const log = window.prompt("Enter log message for this action (optional):")
    if (log === null) return

    setLoading(true)
    crawlers("/claim-logs/update-status/", "PUT", { id: claim.id, status, log })
      .then(() => {
        setSelectedClaim((current) => (current && current.id === claim.id
          ? { ...current, status } : current))
        fetchClaims(offset, selectedStatus.value2, search)
      })
      .catch(() => {
        setLoading(false)
        toast.error("The status could not be updated.", errorFilledToastOptions)
      })
  }

  /** What the row is tinted by: still waiting on somebody, or finished with. */
  const statusColor = (claim: ClaimLog) => {
    if (claim.status === CLAIM_LOG_STATUS.CLOSED) return "#c3e6cb"
    if (claim.status === CLAIM_LOG_STATUS.IN_PROGRESS) return "#ffeeba"
    return "#f5c6cb"
  }

  /**
   * The pills that move a claim, with the log written on it so far in the tooltip.
   *
   * On the row rather than behind an expander: the whole point of the screen is a queue somebody works
   * through, and a status two clicks away is one nobody keeps up to date.
   */
  const renderActions = (claim: ClaimLog) => (
    <KTooltip
      backgroundColor="black"
      position="bottom"
      content={
        <div className="flex flex-col gap-1">
          <KSpan color="white" text={claim.log || "-"} />
        </div>
      }
      children={
        <div className="flex gap-1.5">
          {statusActions.map((action) => (
            <KButton
              key={`claim_status_${claim.id}_${action.value2}`}
              dataTestId="admin-claim-log-status"
              /* Wide enough for "In Progress" to stay on one line: wrapped, it made that row taller
                 than the rest and left the queue looking ragged. */
              width="86px"
              height="30px"
              fontSize={11}
              text={action.label}
              onClick={() => moveTo(claim, action.value2 as string)}
              background={claim.status === action.value2 ? "#000000da" : "#00000011"}
              textColor={claim.status === action.value2 ? "white" : "#666666"}
            />
          ))}
        </div>
      }
    />
  )

  const openLink = (url: string) => window.open(url, "_blank", "noopener,noreferrer")

  const renderDetail = (claim: ClaimLog) => (
    <div className="flex flex-col gap-2 pb-4">
      <KSpan text="Reported uses" fontWeight={500} />
      {claim.links.map((link) => (
        <KButton
          key={`link-${claim.id}-${link}`}
          text={link}
          onClick={() => openLink(link)}
          background="transparent"
          shadowDisabled
          width="auto"
          height="auto"
          padding="0px"
          fontSize={12}
          textColor="#2563EB"
        />
      ))}
      {!claim.links.length && <KSpan text="-" />}
      {claim.note && (
        <>
          <KSpan text="Artist's note" fontWeight={500} />
          <KSpan text={claim.note} />
        </>
      )}
      {claim.log && (
        <>
          <KSpan text="Log" fontWeight={500} />
          <KSpan text={claim.log} />
        </>
      )}
    </div>
  )

  const renderClaims = () => (
    <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
      <div
        className="pb-2 gap-2 items-center"
        style={{ display: "grid", gridTemplateColumns: COLUMN_TEMPLATE }}
      >
        {COLUMNS.map((header) => (
          <div key={`header_${header}`} style={cellStyle}>
            <KSpan fontWeight={500} text={header} />
          </div>
        ))}
      </div>
      {claims.map((claim) => (
        <div key={`claim_${claim.id}`} data-testid="admin-claim-log-row" style={{ width: "100%" }}>
          <div
            className="pt-2 pb-2 gap-2 cursor-pointer items-center"
            style={{
              display: "grid",
              gridTemplateColumns: COLUMN_TEMPLATE,
              borderTop: "1px dashed grey",
              backgroundColor: statusColor(claim)
            }}
            onClick={() => setSelectedClaim(claim)}
          >
            <div style={cellStyle} className="flex gap-1 items-center">
              <KSpan text={String(claim.id)} />
            </div>
            <div style={cellStyle} className="flex gap-1 items-center">
              <KSpan text={claim.created?.substring(0, 10) || "-"} />
            </div>
            {/* The account name and the address under it are both cut short rather than allowed to
                run on: a long address used to reach across into the applicant beside it. */}
            <div style={cellStyle} className="flex flex-col">
              <KSpan text={claim.account || "-"} ellipsis />
              <KSpan text={claim.email || ""} fontSize={12} color="#999" ellipsis />
            </div>
            <div style={cellStyle} className="flex gap-1 items-center">
              <KSpan text={claim.applicantName || "-"} ellipsis />
            </div>
            <div style={cellStyle} className="flex gap-1 items-center">
              <KSpan text={claim.releaseTitle || "-"} ellipsis />
            </div>
            <div style={cellStyle} className="flex flex-col">
              <KSpan text={claim.isrc || "-"} fontSize={12} ellipsis />
              <KSpan text={claim.upc || "-"} fontSize={12} color="#999" ellipsis />
            </div>
            {/* Opening the folder is the whole action: the click must not also open the claim
                behind it and leave the reviewer on a page they did not ask for. */}
            <div
              style={cellStyle}
              className="flex gap-1 items-center"
              onClick={(event) => event.stopPropagation()}
            >
              {claim.driveUrl ? (
                <KButton
                  dataTestId="admin-claim-log-drive"
                  text={`Drive (${claim.documentCount})`}
                  onClick={() => openLink(claim.driveUrl)}
                  background="transparent"
                  shadowDisabled
                  width="auto"
                  height="auto"
                  padding="0px"
                  fontSize={12}
                  textColor="#2563EB"
                />
              ) : (
                // No folder means the upload could not be filed; the documents are still in the
                // support channel with the request, which is where to look for them.
                <KSpan text="In Slack" fontSize={12} color="#999" />
              )}
            </div>
            <div style={cellStyle} className="flex gap-1 items-center">
              <KSpan text={`${claim.links.length} link`} />
            </div>
            {/* The pills sit in the row rather than behind the click that opens the claim, so the
                queue can be worked from the list. Their click must not also open the page. */}
            <div
              style={cellStyle}
              className="flex gap-1 items-center"
              onClick={(event) => event.stopPropagation()}
            >
              {renderActions(claim)}
            </div>
          </div>
        </div>
      ))}
      {!claims.length && !loading && (
        <div className="pt-4">
          <KSpan text="No claim here yet." color="#999" />
        </div>
      )}
    </div>
  )

  /**
   * One claim on its own page: what the artist filed, and the takedown check it came out of.
   *
   * The check is the same Takedown Logs table the DRM screen opens, filtered to this claim - the rows
   * are what says which platforms carried the codes and what was found there, and rebuilding that
   * table here would be a second version of it to keep in step.
   */
  const renderClaimPage = (claim: ClaimLog) => (
    <>
      {renderDetail(claim)}
      <AdminTakedownLog
        claimLogId={claim.id}
        params={props.params}
        setParams={props.setParams}
        headerDiv={<div />}
      />
    </>
  )

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        {selectedClaim ? (
          <KButton
            dataTestId="admin-claim-log-back"
            height="35px"
            width="auto"
            padding="8px 16px"
            text="Back to Claim Logs"
            onClick={() => setSelectedClaim(null)}
          />
        ) : (
          <div className="flex items-center gap-3">
            <div style={{ width: 180 }}>
              <KDropdown
                options={statusOptions}
                selected={selectedStatus}
                placeholder="Status"
                onSelect={(selected) => setSelectedStatus((selected as KSelectOption) || statusOptions[0])}
              />
            </div>
            <div style={{ width: 180 }}>
              <KInput value={search} placeholder="Search UPC" onChange={(value) => setSearch(value)} />
            </div>
            <KButton
              dataTestId="admin-claim-log-apply-filters"
              height="35px"
              width="auto"
              padding="8px 16px"
              text="Apply Filters"
              background="black"
              textColor="white"
              onClick={applyFilters}
            />
          </div>
        )}
      </div>
      {selectedClaim ? renderClaimPage(selectedClaim) : renderClaims()}
      {!selectedClaim && (
        <div className="flex justify-center pb-6">
          <Pagination
            key=""
            hidden={count <= limit}
            size="sm"
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={(pageNumber: number) => {
              setOffset((pageNumber - 1) * limit)
              fetchClaims((pageNumber - 1) * limit, selectedStatus.value2, search)
            }}
          />
        </div>
      )}
    </div>
  )
}

export default AdminClaimLog
